import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {Menu, X, Dumbbell, ChevronDown} from 'lucide-react';

const hubLinks = [
  { label: "The Lean Physique", href: "/lean-physique", accent: "#FF6B35" },
  { label: "Women's Strength", href: "/womens-strength", accent: "#FFA823" },
  { label: "Hardgainers & Ectomorphs", href: "/hardgainers", accent: "#2E86AB" },
];

const navLinks = [
  { label: "Training Hubs", href: "#training-hubs", hasDropdown: true },
  { label: "Articles", href: "#articles" },
  { label: "Calculator", href: "/calculator", isRoute: true },
  { label: "About", href: "#about" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hubsOpen, setHubsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsOpen(false);
    setHubsOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/" + href;
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0F0F1A]/95 backdrop-blur-md shadow-lg shadow-black/30 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
          onClick={() => { setIsOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        >
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#FFA823] flex items-center justify-center group-hover:shadow-lg group-hover:shadow-[#FF6B35]/30 transition-all duration-300">
            <Dumbbell className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span
              className="text-[#F5F5F5] text-lg tracking-wider"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              BUILDING MASS
            </span>
            <span className="text-[#FF6B35] text-[10px] tracking-[0.3em] font-semibold">
              MUSCLE
            </span>
          </div>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.label} className="relative">
                <button
                  onClick={() => setHubsOpen(!hubsOpen)}
                  onMouseEnter={() => setHubsOpen(true)}
                  className="text-[#9CA3AF] hover:text-[#FF6B35] text-sm tracking-wider uppercase transition-colors duration-200 flex items-center gap-1"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {link.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${hubsOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown */}
                <div
                  onMouseLeave={() => setHubsOpen(false)}
                  className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${
                    hubsOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
                  }`}
                >
                  <div className="bg-[#1A1A2E]/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl shadow-black/40 p-2 min-w-[240px]">
                    {hubLinks.map((hub) => (
                      <Link
                        key={hub.label}
                        to={hub.href}
                        onClick={() => setHubsOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors group/item"
                      >
                        <div
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: hub.accent }}
                        />
                        <span className="text-[#9CA3AF] text-sm group-hover/item:text-[#F5F5F5] transition-colors">
                          {hub.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                className="text-[#9CA3AF] hover:text-[#FF6B35] text-sm tracking-wider uppercase transition-colors duration-200"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-[#9CA3AF] hover:text-[#FF6B35] text-sm tracking-wider uppercase transition-colors duration-200"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {link.label}
              </button>
            )
          )}
          <Link
            to="/calculator"
            className="btn-shimmer text-white text-sm font-semibold px-5 py-2.5 rounded-lg tracking-wider uppercase hover:scale-105 transition-transform duration-200"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Free Calculator
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-[#F5F5F5] p-2"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-[#1A1A2E]/95 backdrop-blur-md border-t border-white/5 px-6 py-4 flex flex-col gap-1">
          {/* Training Hubs section */}
          <div className="py-2">
            <span
              className="text-[#666] text-[10px] tracking-[0.2em] uppercase font-semibold block mb-2"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Training Hubs
            </span>
            {hubLinks.map((hub) => (
              <Link
                key={hub.label}
                to={hub.href}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 py-2 text-[#9CA3AF] hover:text-[#F5F5F5] text-sm transition-colors"
              >
                <div
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: hub.accent }}
                />
                {hub.label}
              </Link>
            ))}
          </div>

          <div className="h-px bg-white/5 my-1" />

          {/* Other links */}
          {navLinks.filter((l) => !l.hasDropdown).map((link) =>
            link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setIsOpen(false)}
                className="text-[#9CA3AF] hover:text-[#FF6B35] text-sm tracking-wider uppercase text-left py-2 transition-colors"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-[#9CA3AF] hover:text-[#FF6B35] text-sm tracking-wider uppercase text-left py-2 transition-colors"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {link.label}
              </button>
            )
          )}

          <Link
            to="/calculator"
            onClick={() => setIsOpen(false)}
            className="btn-shimmer text-white text-sm font-semibold px-5 py-2.5 rounded-lg tracking-wider uppercase mt-2 text-center"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Free Calculator
          </Link>
        </div>
      </div>
    </nav>
  );
}
